import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useMe } from 'contexts/me'
import { useDocuments } from 'contexts/documents'
import { useCombineAccessUnits, useEnrichUnit, splitAccessUnits } from 'pages/documents/utilities'
import { prune } from 'utilities/array'
import Modal from 'modals/dialog'
import Form from 'components/form/controller'
import AccessUnitsField from 'components/form/field/access-units'
import QuickSelect from 'pages/documents/components/quick-select-access-units'
import CheckboxField from 'components/form/field/checkbox'
import Message, { InlineMessage } from 'components/message'
import WordList from 'components/word-list'
import Caption from 'components/typography/caption'
import Status from './status'
import Actions from './actions'

const BulkEditDocumentAccess = ({ act, failed, progress, dismiss, salt }) => {
    const { formatMessage } = useIntl()

    const { getListMe } = useMe()

    const { getEditableSelection } = useDocuments()

    const combineAccessUnits = useCombineAccessUnits()
    const enrichUnit = useEnrichUnit()

    const [replace, setReplace] = useState(false)
    const [whistle, setWhistle] = useState(Date.now())
    const [access, setAccess] = useState({
        value: [],
        changed: false
    })

    const setBulkAccess = (body, ...rest) => void act({
        ...splitAccessUnits(access.value, true),
        replace
    }, ...rest)

    const selection = getEditableSelection()

    const unitsByHighestAccessInSelection = selection
        .map(dokument => ({
            documentId: dokument.id,
            accessUnits: combineAccessUnits(dokument)
        }))
        .reduce((accumulator, { documentId, accessUnits }) => ({
            ...accumulator,
            ...accessUnits.reduce((units, { unit, access }) => ({
                ...units,
                [unit.id]: {
                    unit,
                    access: {
                        share: !!access.share || !!accumulator[unit.id]?.access?.share,
                        owner: !!access.owner || !!accumulator[unit.id]?.access?.owner
                    },
                    documentIds: prune([
                        ...(accumulator[unit.id]?.documentIds ?? []),
                        documentId
                    ])
                }
            }), {})
        }), {})

    const degradedUnits = access.value
        .filter(({ unit, access }) => {
            const highestAccess = unitsByHighestAccessInSelection[unit.id]?.access
            return (!!highestAccess?.owner && !access.owner) || (!!highestAccess?.share && !access.share)
        })
        .map(({ unit }) => unitsByHighestAccessInSelection[unit.id])

    const degradedUnitsDocumentCount = prune(degradedUnits.flatMap(({ documentIds }) => documentIds)).length
    const noOwners = !access.value.some(({ access }) => access.owner)

    return (
        <Form
            layout="vertical"
            onSubmit={setBulkAccess}
            key={`${salt}:bulk:access`}>
            <Modal.Body>
                <CheckboxField
                    salt={salt}
                    label={formatMessage({
                        id: 'documents_bulk_edit_shares_replace_label',
                        defaultMessage: 'Overwrite'
                    })}
                    name="replace"
                    field={{
                        value: replace,
                        include: 'always'
                    }}
                    interaction="switch"
                    onChange={({ replace }) => setReplace(replace)}
                    enabled={progress.processed === 0} />
                {!replace && (
                    <Caption>
                        <FormattedMessage
                            id="documents_bulk_edit_access_extend_description"
                            defaultMessage="Your specified access settings will be combined with each document’s current settings. Any conflicts will be resolved by using the new access settings you provide." />
                    </Caption>
                )}
                {!!replace && (
                    <Caption>
                        <FormattedMessage
                            id="documents_bulk_edit_access_replace_description"
                            defaultMessage="Document access will be replaced completely with what you specify here." />
                    </Caption>
                )}
                <AccessUnitsField
                    salt={salt}
                    label={formatMessage({
                        id: 'noun_access',
                        defaultMessage: 'Access'
                    })}
                    name="access"
                    field={{
                        value: access.value,
                        include: 'always'
                    }}
                    onChange={({ access }) => setAccess({
                        value: access.map(({ unit, ...rest }) => ({
                            ...rest,
                            unit: enrichUnit(unit)
                        })),
                        changed: true
                    })}
                    formatAdminTooltip={unit => formatMessage({
                        id: 'document_unit_administrator_description',
                        defaultMessage: 'Users with roles that grant document access in <strong>{name}</strong>'
                    }, unit)}
                    {...(replace ? {
                        belowSearchContent: (
                            <QuickSelect
                                options={[{
                                    unit: getListMe(),
                                    access: {
                                        share: true,
                                        owner: true
                                    }
                                }]}
                                values={access.value}
                                insertValue={value => {
                                    setAccess({
                                        value: [
                                            value,
                                            ...access.value
                                        ],
                                        changed: true
                                    })

                                    setWhistle(Date.now())
                                }} />
                        )
                    } : null)}
                    enabled={progress.processed === 0}
                    whistle={whistle} />
                {(!replace && !!degradedUnits.length) && (
                    <InlineMessage
                        type="info"
                        className="spacious"
                        message={formatMessage({
                            id: 'documents_bulk_edit_access_degraded_units_info',
                            defaultMessage: '{count, plural, =0 {} =1 {{units} will end up with less access to 1 document.} other {{units} will end up with less access to {count} documents.}}'
                        }, {
                            count: degradedUnitsDocumentCount,
                            units: (
                                <WordList
                                    words={degradedUnits.map(({ unit }) => unit.name)}
                                    wordRenderer={name => <strong>{name}</strong>} />
                            )
                        })} />
                )}
                {(!!replace && access.changed && noOwners) && (
                    <Message
                        type="warning"
                        message={formatMessage({
                            id: 'document_access_owners_missing_warning',
                            defaultMessage: 'At least one unit needs to have <strong>See and edit<\/strong> access.'
                        })} />
                )}
                <Status
                    progress={progress}
                    failed={failed} />
            </Modal.Body>
            <Actions
                progress={progress}
                submissible={access.changed && (!replace || (!!replace && !noOwners))}
                dismiss={dismiss} />
        </Form>
    )
}

export default BulkEditDocumentAccess